"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Icon } from "@/lib/icons/Icon";
import { FormStatus, Input } from "@/components/ui/Form";
import { Badge, type BadgeVariant } from "@/components/ui/Badge/Badge";
import { toast } from "@/lib/notifications/toast";
import { confirmDialog } from "@/lib/notifications/confirmDialog";
import {
  fetchUserProcedures,
  fetchProcedureDetail,
  submitProcedureDecision,
  PROCEDURE_ACCEPT,
  PROCEDURE_REJECT,
  type UserProcedure,
  type UserProcedureDetail,
} from "@/lib/api/c-procedures-user";
import styles from "./CProcedures.module.css";

const PAGE_SIZE = 20;

/**
 * Plugin procedur usera (shell c_user_accounts) - REST/React.
 *
 * Akcja hash-routable (z `sharingContentForApi`): `get-procedures`.
 * Parytet z legacy `C_PROCEDURES_CONTROLLER` - lista procedur dostepnych dla grup
 * usera, podglad tresci + pliku, akceptacja/odrzucenie.
 *
 * **Grid:** TanStack Table v8 (headless) - caly bounded zbior, sort/filtr/paginacja
 * client-side. Podglad otwiera panel szczegolow pod tabela (oznacza procedure jako
 * otwarta po stronie backendu) z przyciskami akceptacji/odrzucenia.
 */

/** Wariant Badge dla statusu akceptacji. */
function statusVariant(status: number): BadgeVariant {
  if (status === PROCEDURE_ACCEPT) return "success";
  if (status === PROCEDURE_REJECT) return "danger";
  return "neutral";
}

/** Etykieta statusu akceptacji (parytet z legacy `[status]` z c_procedures_pl.lang). */
function statusLabel(status: number): string {
  if (status === PROCEDURE_ACCEPT) return "Potwierdzona";
  if (status === PROCEDURE_REJECT) return "Odrzucona";
  return "Nowa";
}

function formatDate(raw: string): string {
  if (!raw) return "";
  return raw.split(" ")[0] || raw;
}

export function CProceduresView({ onDecision }: { onDecision?: () => void } = {}) {
  const [rows, setRows] = useState<UserProcedure[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "deadline", desc: true }]);

  const [detail, setDetail] = useState<UserProcedureDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deciding, setDeciding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { procedures, truncated: tr } = await fetchUserProcedures();
      setRows(procedures);
      setTruncated(tr);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const d = await fetchProcedureDetail(id);
      setDetail(d);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Nie udało się pobrać procedury.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const decide = useCallback(
    async (decision: "accept" | "reject") => {
      if (!detail) return;
      const ok = await confirmDialog({
        title: detail.name,
        message:
          decision === "accept"
            ? "Czy na pewno chcesz zaakceptować tę procedurę?"
            : "Czy na pewno chcesz odrzucić tę procedurę?",
        variant: decision === "accept" ? "info" : "warning",
        confirmLabel: decision === "accept" ? "Akceptuję" : "Odrzucam",
        cancelLabel: "Anuluj",
      });
      if (!ok) return;

      setDeciding(true);
      try {
        const status = await submitProcedureDecision(detail.id, decision);
        setDetail((prev) => (prev ? { ...prev, status } : prev));
        toast.success(decision === "accept" ? "Procedura zaakceptowana." : "Procedura odrzucona.");
        await load();
        /* Powiadom shell konta (bramka procedur) zeby przeliczyl blokade. */
        onDecision?.();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Nie udało się zapisać decyzji.");
      } finally {
        setDeciding(false);
      }
    },
    [detail, load, onDecision],
  );

  const columns = useMemo(() => {
    const col = createColumnHelper<UserProcedure>();
    return [
      col.accessor("name", {
        id: "name",
        header: () => "Procedura",
        cell: (ctx) => <span className={styles.docName}>{ctx.getValue()}</span>,
      }),
      col.accessor("parent_name", {
        id: "parent",
        header: () => "Grupa",
        cell: (ctx) => ctx.getValue() || "-",
      }),
      col.accessor("required", {
        id: "required",
        header: () => "Wymagana",
        cell: (ctx) => (
          <Badge variant={ctx.getValue() ? "warning" : "neutral"}>
            {ctx.getValue() ? "Tak" : "Nie"}
          </Badge>
        ),
      }),
      col.accessor("status", {
        id: "status",
        header: () => "Status",
        cell: (ctx) => (
          <Badge variant={statusVariant(ctx.getValue())}>{statusLabel(ctx.getValue())}</Badge>
        ),
      }),
      col.accessor("deadline", {
        id: "deadline",
        header: () => "Termin",
        cell: (ctx) => formatDate(ctx.getValue()),
      }),
      col.display({
        id: "action",
        header: () => <span className="sr-only">Akcje</span>,
        cell: (ctx) => (
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => void openDetail(ctx.row.original.id)}
            title="Podgląd procedury"
          >
            <Icon name="eye" className={styles.actionIcon} />
            Podgląd
          </button>
        ),
      }),
    ];
  }, [openDetail]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;

  return (
    <div className={styles.root}>
      <p className={styles.note}>Procedury udostępnione dla Twoich grup.</p>

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <Input
            type="search"
            prefix={<Icon name="magnifying-glass" />}
            placeholder="Szukaj (nazwa, grupa)..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            aria-label="Szukaj procedur"
          />
        </div>
      </div>

      {loading && <FormStatus variant="info">Wczytywanie procedur...</FormStatus>}
      {error && !loading && <FormStatus variant="error">{error}</FormStatus>}

      {!loading && !error && rows.length === 0 && (
        <FormStatus variant="info">Brak procedur do wyświetlenia.</FormStatus>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sorted = header.column.getIsSorted();
                      return (
                        <th
                          key={header.id}
                          className={canSort ? styles.thSortable : undefined}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          aria-sort={
                            sorted === "asc"
                              ? "ascending"
                              : sorted === "desc"
                                ? "descending"
                                : undefined
                          }
                        >
                          <span className={styles.thInner}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && (
                              <Icon
                                name={sorted === "desc" ? "caret-down" : "caret-up"}
                                className={sorted ? styles.sortIconActive : styles.sortIcon}
                              />
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {truncated && (
            <p className={styles.truncatedHint}>
              Wyświetlono pierwsze 500 procedur. Zawęź wyszukiwanie aby znaleźć kolejne.
            </p>
          )}

          {pageCount > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <Icon name="caret-left" />
                Poprzednia
              </button>
              <span className={styles.pageInfo}>
                Strona {pageIndex + 1} z {pageCount}
              </span>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Następna
                <Icon name="caret-right" />
              </button>
            </div>
          )}
        </>
      )}

      {detailLoading && <FormStatus variant="info">Wczytywanie procedury...</FormStatus>}

      {detail && !detailLoading && (
        <div className={styles.detail}>
          <div className={styles.detailHeader}>
            <div>
              <h3 className={styles.detailTitle}>{detail.name}</h3>
              {detail.parent_name && (
                <span className={styles.detailParent}>Grupa: {detail.parent_name}</span>
              )}
            </div>
            <button
              type="button"
              className={styles.detailClose}
              onClick={() => setDetail(null)}
              aria-label="Zamknij podgląd"
            >
              <Icon name="x" />
            </button>
          </div>

          <div className={styles.detailBody}>{detail.description}</div>

          <div className={styles.detailActions}>
            {detail.file_href && (
              <a
                href={detail.file_href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.fileBtn}
              >
                <Icon name="file-text" className={styles.actionIcon} />
                Pobierz plik
              </a>
            )}

            {detail.status === PROCEDURE_ACCEPT ? (
              <Badge variant="success">Zaakceptowano</Badge>
            ) : (
              <>
                <button
                  type="button"
                  className={styles.acceptBtn}
                  onClick={() => void decide("accept")}
                  disabled={deciding}
                >
                  <Icon name="check" className={styles.actionIcon} />
                  Akceptuję
                </button>
                <button
                  type="button"
                  className={styles.rejectBtn}
                  onClick={() => void decide("reject")}
                  disabled={deciding}
                >
                  <Icon name="x" className={styles.actionIcon} />
                  Odrzucam
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Czy plugin obsluguje dana akcje (registry w ExtensionActionView).
 */
export function cProceduresHandlesAction(actionId: string): boolean {
  return actionId === "get-procedures";
}
